import _extends from '@babel/runtime/helpers/extends';
import React__default from 'react';
import PT from 'prop-types';
import { InlineMessage } from '@amzn/storm-ui';
import { F as FeedbackFormField } from './FeedbackFormField-BQYTqZo2.js';
import { f as formatMessageHelper } from './i18n/formatMessageHelper-CMOV131q.js';
import { u as useIntl } from '../LocalModules/storm-ui-intl/dist/esm/3rdPartyModules/react-intl/lib/src/components/useIntl-BGx4v_Uj-CJNeXUZQ.js';
import { g as getCharactersCount } from '../internalComponents/TextAreaWithCharacterCount/utils/getCharactersCount-D5D88kic.js';
import { T as TextAreaWithCharacterCount } from '../internalComponents/TextAreaWithCharacterCount/TextAreaWithCharacterCount-JpWVe5yb.js';
import { n as noop } from '../LocalModules/storm-ui-utils/dist/esm/noop-BNkHXPFl-BNkHXPFl.js';

const TextAreaForFeedbackForm = ({
  defaultValue,
  disabled,
  id,
  maxGlyphCount,
  name,
  onChange,
  renderMessage,
  validateOnMount,
  ...restProps
}) => {
  const {
    formatMessage,
    formatNumber
  } = useIntl();
  const formatCharactersRemaining = React__default.useCallback(charactersRemaining => {
    try {
      return formatNumber(charactersRemaining);
    } catch (error) {
      return `${charactersRemaining}`;
    }
  }, [formatNumber]);
  const getCharCountErrorMessage = React__default.useCallback(charactersRemaining => formatMessage(formatMessageHelper('textAreaForFeedbackFormGetCharCountErrorMessage'), {
    charactersToBeDeleted: formatCharactersRemaining(Math.abs(charactersRemaining))
  }), [formatCharactersRemaining, formatMessage]);
  const defaultRenderMessage = React__default.useCallback((characters, max) => {
    const charactersRemaining = max - characters;
    return /*#__PURE__*/React__default.createElement(InlineMessage, {
      messageType: charactersRemaining < 0 ? 'error' : undefined,
      message: charactersRemaining >= 0 ? formatMessage(formatMessageHelper('textAreaForFeedbackFormCharactersRemaining'), {
        charactersRemaining: formatCharactersRemaining(charactersRemaining)
      }) : getCharCountErrorMessage(charactersRemaining)
    });
  }, [formatCharactersRemaining, formatMessage, getCharCountErrorMessage]);
  const rules = React__default.useMemo(() => ({
    validate: {
      maxLength: value => {
        const charactersRemaining = maxGlyphCount - getCharactersCount(value);
        if (charactersRemaining < 0) {
          return getCharCountErrorMessage(charactersRemaining);
        }
        return true;
      }
    }
  }), [maxGlyphCount, getCharCountErrorMessage]);
  return /*#__PURE__*/React__default.createElement(FeedbackFormField, {
    validateOnMount: validateOnMount,
    name: name,
    id: id,
    defaultValue: defaultValue !== null && defaultValue !== void 0 ? defaultValue : '',
    rules: rules
  }, ({
    id: inputId,
    isSubmitting,
    error,
    onChange: internalOnChange,
    value: internalValue
  }) => {
    const errorMessage = error === null || error === void 0 ? void 0 : error.message;
    const handleChange = event => {
      internalOnChange(event.target.value);
      if (typeof onChange === 'function') {
        onChange(event);
      }
    };
    return /*#__PURE__*/React__default.createElement(TextAreaWithCharacterCount, _extends({}, restProps, {
      fullWidth: true,
      disabled: disabled || isSubmitting,
      id: inputId,
      maxGlyphCount: maxGlyphCount,
      onChange: handleChange,
      renderMessage: renderMessage !== null && renderMessage !== void 0 ? renderMessage : defaultRenderMessage,
      value: internalValue
    }, errorMessage ? {
      'aria-invalid': 'true'
    } : {
      'aria-invalid': 'false'
    }, {
      statusType: errorMessage ? 'error' : undefined
    }));
  });
};
TextAreaForFeedbackForm.propTypes = {
  defaultValue: PT.oneOfType([PT.string, PT.number]),
  disabled: PT.bool,
  getCharactersCount: PT.func,
  id: PT.string.isRequired,
  label: PT.string.isRequired,
  maxGlyphCount: PT.number.isRequired,
  name: PT.string.isRequired,
  onChange: PT.func,
  renderMessage: PT.func,
  validateOnMount: PT.bool,
  className: PT.string,
  hideLabel: PT.bool,
  renderLabelStart: PT.oneOfType([PT.func, PT.node]),
  renderLabelEnd: PT.oneOfType([PT.func, PT.node]),
  inline: PT.bool,
  placeholder: PT.string,
  inputRef: PT.func,
  help: PT.oneOfType([PT.string, PT.node]),
  messageE2EId: PT.string
};
TextAreaForFeedbackForm.defaultProps = {
  defaultValue: undefined,
  disabled: false,
  getCharactersCount: undefined,
  validateOnMount: true,
  renderMessage: undefined,
  onChange: noop,
  hideLabel: false,
  renderLabelStart: undefined,
  renderLabelEnd: undefined,
  inline: false,
  placeholder: '',
  help: undefined,
  inputRef: () => null,
  messageE2EId: undefined,
  className: ''
};

export { TextAreaForFeedbackForm as T };
//# sourceMappingURL=TextAreaForFeedbackForm-CjMerzkQ.js.map
